import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, Mail, Edit, FileCheck, MapPin, Plane, Wallet, Plus, Trash2, Receipt as ReceiptIcon, Send, Pencil, History, RefreshCcw, Copy, ShieldCheck, MessageCircle } from 'lucide-react';
import { formatDate, caymanToday, caymanDateKey } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';
import { formatDiscountBadge } from '../../utils/discountMath';
import { IssueRefundModal } from '../../components/admin/IssueRefundModal';
import { QuoteTrashNotice } from '../../components/admin/QuoteTrashNotice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHOD_LABELS = {
  bank_transfer: 'Bank Transfer',
  wire: 'Wire Transfer',
  cash: 'Cash',
  card: 'Credit/Debit Card',
  other: 'Other',
};

const DEPOSIT_BADGE = {
  none: { label: 'No Deposit', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  pending: { label: 'Deposit Pending', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  partial: { label: 'Partial Payment', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  received: { label: 'Deposit Received', cls: 'bg-green-50 text-green-800 border-green-200' },
  full: { label: 'Paid in Full', cls: 'bg-green-50 text-green-800 border-green-300' },
};

export const ViewQuote = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);
  const [historyPayment, setHistoryPayment] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${id}`, {
        cache: 'no-store',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setQuote(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      } else {
        navigate('/admin/quotes');
      }
    } catch (err) {
      logger.error('Failed to fetch quote:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm('Duplicate this quote? A new draft with the same line items and discounts will be created (client + pet info will be blank).')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (response.ok) {
        const q = await response.json();
        navigate(`/admin/quotes/${q.id}/edit`);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to duplicate: ${err.detail || response.status}`);
      }
    } catch (err) {
      logger.error('Duplicate quote failed:', err);
      alert('Network error. Please try again.');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${id}/download-pdf`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Quote_${quote.client_name.replace(/\s/g, '_')}_${quote.quote_number.replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (err) {
      logger.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleDownloadSignatureAudit = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${id}/signature-audit-pdf`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const qnum = (quote.quote_number || id.slice(0, 8)).replace(/\//g, '-');
        a.download = `Signature_Audit_Quote_${qnum}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Failed to download signature audit');
      }
    } catch (err) {
      logger.error('Signature audit download error:', err);
      alert('Failed to download signature audit. Please try again.');
    }
  };

  const handleSendEmail = async () => {
    if (!window.confirm(`Send quote to ${quote.client_email}?`)) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${id}/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        alert(`Quote sent successfully to ${quote.client_email}!`);
        fetchQuote(); // Refresh to update status
      } else {
        const error = await response.json();
        alert(`Failed to send email: ${error.detail}`);
      }
    } catch (err) {
      alert('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleSendWhatsApp = async () => {
    // If already accepted, no point sending a fresh acceptance link
    const alreadySigned = quote?.acceptance?.typed_name || ['accepted', 'paid'].includes(quote?.status);
    try {
      // Mint (or fetch existing) acceptance token
      const res = await fetch(`${API_URL}/api/admin/quotes/${id}/acceptance-link`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (!res.ok) {
        alert('Failed to prepare acceptance link. Please try again.');
        return;
      }
      const data = await res.json();
      const origin = window.location.origin;
      const publicUrl = `${origin}${data.url}`;

      // Fetch the admin-editable template (falls back to defaults if the
      // request fails or the admin hasn't customised anything yet).
      let templateBody = '';
      try {
        const tRes = await fetch(`${API_URL}/api/admin/whatsapp-templates`, {
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
        });
        if (tRes.ok) {
          const tData = await tRes.json();
          const key = alreadySigned ? 'thanks' : 'quote';
          templateBody = tData.templates?.[key] || tData.defaults?.[key] || '';
        }
      } catch (e) {
        logger.error('Fetch WA templates:', e);
      }
      if (!templateBody) {
        // Hard-coded fallback matches the historic copy
        templateBody = alreadySigned
          ? `Hi {{first_name}}! Here's your signed Pawsome Ventures quote {{quote_number}} for your records:\n\n{{acceptance_url}}\n\n— Pawsome Ventures`
          : `Hi {{first_name}}! Your Pawsome Ventures quote {{quote_number}} is ready for review — {{currency}}{{total}}.\n\nOne-tap review + e-signature (no account needed):\n{{acceptance_url}}\n\n{{valid_until_line}}— Pawsome Ventures`;
      }

      // Resolve phone
      let phone = (quote?.client_phone || '').replace(/\D/g, '');
      if (!phone) {
        const p = window.prompt('No phone number on file. Enter recipient phone (with country code, digits only):');
        phone = (p || '').replace(/\D/g, '');
        if (!phone) return;
      }

      const firstName = (quote?.client_name || '').split(' ')[0] || 'there';
      const currency = quote?.currency || 'KYD';
      const total = Number(quote?.total || 0).toFixed(2);
      const qnum = quote?.quote_number || id.slice(0, 8);
      const validUntil = (quote?.valid_until || '').slice(0, 10);
      const vars = {
        first_name: firstName,
        quote_number: qnum,
        currency,
        total,
        acceptance_url: publicUrl,
        valid_until_line: validUntil ? `Valid until ${validUntil}. ` : '',
      };
      const text = templateBody.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : `{{${k}}}`));
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
      window.open(waUrl, '_blank', 'noopener');
    } catch (err) {
      logger.error('WhatsApp send error:', err);
      alert('Failed to prepare WhatsApp link. Please try again.');
    }
  };

  const handleConvertToInvoice = async () => {
    if (!window.confirm('Convert this quote to an invoice? This action cannot be undone.')) return;
    
    setConverting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${id}/convert-to-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const invoice = await response.json();
        alert(`Quote converted to Invoice ${invoice.invoice_number}!`);
        navigate(`/admin/invoices/${invoice.id}`);
      } else {
        const error = await response.json();
        alert(`Failed to convert: ${error.detail}`);
      }
    } catch (err) {
      alert('Failed to convert quote. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Remove this payment? If a receipt was auto-issued, it will also be deleted.')) return;
    setDeletingPaymentId(paymentId);
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${id}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (response.ok) {
        const data = await response.json();
        setQuote(data);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to delete payment: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Delete payment error:', err);
      alert('Failed to delete payment. Please try again.');
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const handleDownloadPaymentReceipt = async (receiptId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${receiptId}/download-pdf`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (!response.ok) {
        alert('Failed to download receipt PDF');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Receipt_${receiptId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      logger.error('Download receipt error:', err);
      alert('Failed to download receipt.');
    }
  };

  const [issuingReceiptId, setIssuingReceiptId] = useState(null);

  const handleIssueReceipt = async (payment) => {
    const existing = Boolean(payment.receipt_issued);
    const confirmMsg = existing
      ? `Re-send receipt ${payment.receipt_number} to ${quote.client_email}?`
      : `Issue a receipt for this payment and email it to ${quote.client_email}?`;
    if (!window.confirm(confirmMsg)) return;

    setIssuingReceiptId(payment.id);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/quotes/${id}/payments/${payment.id}/issue-receipt`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${btoa(':' + password)}`,
          },
          body: JSON.stringify({ send_email: true }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setQuote(data);
        const issued = (data.payments || []).find((p) => p.id === payment.id);
        alert(
          existing
            ? `Receipt ${issued?.receipt_number || ''} re-sent to ${quote.client_email}.`
            : `Receipt ${issued?.receipt_number || ''} created and emailed to ${quote.client_email}.`,
        );
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to issue receipt: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Issue receipt error:', err);
      alert('Failed to issue receipt. Please try again.');
    } finally {
      setIssuingReceiptId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      sent: 'bg-blue-100 text-blue-800 border-blue-200',
      accepted: 'bg-green-100 text-green-800 border-green-200',
      declined: 'bg-red-100 text-red-800 border-red-200',
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground/60">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (!quote) return null;

  const isExpired = new Date(quote.valid_until) < new Date();

  if (quote?.deleted === true) {
    return <QuoteTrashNotice quote={quote} onRestored={fetchQuote} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4 admin-header-row">
            <div>
              <h1 className="text-2xl font-bold text-primary">Quote {quote.quote_number}</h1>
              <p className="text-sm text-foreground/60">View quote details</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/admin/quotes')}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={handleDownloadPDF}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
              {quote.acceptance?.typed_name && (
                <button
                  onClick={handleDownloadSignatureAudit}
                  data-testid="signature-audit-btn"
                  title="Download the signature certificate of completion (typed name, cursive rendering, IP, timestamp, audit trail)"
                  className="px-4 py-2 text-sm border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-2"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Signature Audit
                </button>
              )}
              <button
                onClick={() => navigate(`/admin/quotes/${id}/edit`)}
                className="px-4 py-2 text-sm border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleDuplicate}
                data-testid="duplicate-quote-btn"
                title="Create a new draft quote with the same line items and discounts"
                className="px-4 py-2 text-sm border border-border text-foreground rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Duplicate
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sending}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Mail className="w-4 h-4" />
                {sending ? 'Sending...' : 'Email Quote'}
              </button>
              <button
                onClick={() => navigate(`/admin/email-composer?attach=quote:${id}&to=${encodeURIComponent(quote.client_email || '')}&subject=${encodeURIComponent(`Quote ${quote.quote_number} from Pawsome Ventures`)}`)}
                data-testid="compose-with-quote-btn"
                title="Open the composer with this quote pre-attached (add other documents, CC teammates, upload photos, etc.)"
                className="px-4 py-2 text-sm border-2 border-blue-500 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Compose…
              </button>
              <button
                onClick={handleSendWhatsApp}
                data-testid="whatsapp-quote-btn"
                title="Send the quote review + sign link over WhatsApp"
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </button>
              {!quote.converted_to_invoice && (
                <button
                  onClick={handleConvertToInvoice}
                  disabled={converting}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <FileCheck className="w-4 h-4" />
                  {converting ? 'Converting...' : 'Convert to Invoice'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="col-span-2 space-y-6">
            {/* Status Banner */}
            {quote.acceptance?.typed_name && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start justify-between gap-4" data-testid="quote-accepted-banner">
                <div className="flex items-start gap-3 min-w-0">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-emerald-900">Digitally signed by {quote.acceptance.typed_name}</p>
                    <p className="text-sm text-emerald-800">
                      Signed on {formatDate(quote.acceptance.signed_at)} · Document ID <span className="font-mono">{quote.acceptance.document_id || '—'}</span>
                    </p>
                    {quote.acceptance.ip && (
                      <p className="text-xs text-emerald-700 mt-0.5">IP {quote.acceptance.ip}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleDownloadSignatureAudit}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-semibold flex items-center gap-1.5 flex-shrink-0"
                  data-testid="quote-accepted-banner-audit-btn"
                >
                  <ShieldCheck className="w-4 h-4" /> Audit PDF
                </button>
              </div>
            )}
            {quote.converted_to_invoice && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileCheck className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-900">Converted to Invoice</p>
                    <p className="text-sm text-green-700">This quote has been converted to an invoice</p>
                  </div>
                </div>
                {quote.invoice_id && (
                  <button
                    onClick={() => navigate(`/admin/invoices/${quote.invoice_id}`)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    View Invoice
                  </button>
                )}
              </div>
            )}

            {isExpired && !quote.converted_to_invoice && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="font-semibold text-red-900">⚠️ Quote Expired</p>
                <p className="text-sm text-red-700">This quote expired on {formatDate(quote.valid_until)}</p>
              </div>
            )}

            {/* Quote Details */}
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <h2 className="text-lg font-bold text-foreground mb-4">Quote Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-foreground/60">Quote Number</p>
                  <p className="font-mono font-semibold text-primary">{quote.quote_number}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Status</p>
                  <div className="mt-1">{getStatusBadge(quote.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Quote Date</p>
                  <p className="font-medium text-foreground">{formatDate(quote.quote_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Valid Until</p>
                  <p className={`font-medium ${isExpired ? 'text-red-600' : 'text-foreground'}`}>
                    {formatDate(quote.valid_until)}
                  </p>
                </div>
              </div>
            </div>

            {/* Travel Details */}
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Plane className="w-5 h-5" />
                Travel Details
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-primary mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-foreground/60">Route</p>
                    <p className="font-medium text-foreground text-lg">
                      {quote.origin} → {quote.destination}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <svg className="w-5 h-5 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-foreground/60">Animal(s)</p>
                    <p className="font-medium text-foreground">{quote.animal_names}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <h2 className="text-lg font-bold text-foreground mb-4">Services</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Service</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-foreground/60 uppercase">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Price</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {quote.items.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-foreground/70">{index + 1}</td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          <div>{item.description}</div>
                          {item.discount_type && item.discount_value > 0 && (
                            <div className="text-xs text-red-600 mt-0.5" data-testid={`quote-item-discount-badge-${index}`}>
                              {formatDiscountBadge(item, quote.currency)}{item.discount_label ? ` — ${item.discount_label}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-foreground/70">{item.quantity}</td>
                        <td className="px-4 py-3 text-sm text-right text-foreground/70">{quote.currency}{item.unit_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-foreground">{quote.currency}{item.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-primary/5 border-t-2 border-primary">
                    {quote.discount_type && (quote.discount_amount || 0) > 0 && (
                      <>
                        <tr>
                          <td colSpan="4" className="px-4 py-2 text-right text-sm text-foreground/70">Subtotal:</td>
                          <td className="px-4 py-2 text-right text-sm font-semibold text-foreground" data-testid="quote-subtotal">
                            {quote.currency}{(quote.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                        <tr data-testid="quote-doc-discount-row">
                          <td colSpan="4" className="px-4 py-2 text-right text-sm text-red-700">
                            Discount{quote.discount_type === 'percent' ? ` (${quote.discount_value}%)` : ''}{quote.discount_label ? ` — ${quote.discount_label}` : ''}:
                          </td>
                          <td className="px-4 py-2 text-right text-sm font-semibold text-red-700">
                            −{quote.currency}{(quote.discount_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td colSpan="4" className="px-4 py-4 text-right font-bold text-foreground">TOTAL:</td>
                      <td className="px-4 py-4 text-right font-bold text-2xl text-primary">{quote.currency}{quote.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Deposit / Payment Ledger */}
            {(() => {
              const payments = quote.payments || [];
              const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
              const remaining = Math.max((quote.total || 0) - totalPaid, 0);
              const pct = quote.total > 0 ? (totalPaid / quote.total) * 100 : 0;
              const badge = DEPOSIT_BADGE[quote.deposit_status || 'none'] || DEPOSIT_BADGE.none;

              return (
                <div className="bg-white rounded-xl border border-border shadow-sm p-6" data-testid="quote-deposit-panel">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-primary" />
                      Deposits & Payments
                    </h2>
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      data-testid="add-payment-btn"
                      className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Record Payment
                    </button>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-foreground/60 uppercase tracking-wide">Status</p>
                      <span className={`inline-block mt-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.cls}`} data-testid="deposit-status-badge">
                        {badge.label}
                      </span>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-foreground/60 uppercase tracking-wide">Total Paid</p>
                      <p className="mt-1 font-semibold text-foreground" data-testid="total-paid">
                        {quote.currency}{totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-xs text-foreground/50">({pct.toFixed(0)}%)</span>
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-foreground/60 uppercase tracking-wide">Remaining</p>
                      <p className="mt-1 font-semibold text-primary" data-testid="balance-remaining">
                        {quote.currency}{remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-foreground/60 uppercase tracking-wide">Last Payment</p>
                      <p className="mt-1 font-medium text-foreground text-sm">
                        {quote.deposit_date ? formatDate(quote.deposit_date) : '—'}
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-6">
                    <div
                      className="h-full bg-primary transition-all duration-500"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>

                  {/* Ledger */}
                  {payments.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
                      <Wallet className="w-10 h-10 text-foreground/30 mx-auto mb-2" />
                      <p className="text-sm text-foreground/60">No payments recorded yet.</p>
                      <p className="text-xs text-foreground/50">Click "Record Payment" above to log a deposit.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="payment-ledger-table">
                        <thead className="bg-gray-50 border-b border-border">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-foreground/60 uppercase">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-foreground/60 uppercase">Method</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-foreground/60 uppercase">Amount</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-foreground/60 uppercase">Receipt</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-foreground/60 uppercase">Notes</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {payments.map((p) => (
                            <tr key={p.id} data-testid={`payment-row-${p.id}`} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-foreground/80">
                                <div className="flex items-center gap-2">
                                  <span>{formatDate(p.payment_date)}</span>
                                  {p.updated_at && (
                                    <button
                                      type="button"
                                      onClick={() => setHistoryPayment(p)}
                                      data-testid={`payment-edited-${p.id}`}
                                      className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                                      title="View change history"
                                    >
                                      Edited
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-foreground/80">{PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method}</td>
                              <td className="px-3 py-2 text-right font-semibold text-foreground">
                                {quote.currency}{(p.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2">
                                {p.receipt_issued && p.receipt_id ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleDownloadPaymentReceipt(p.receipt_id)}
                                      className="inline-flex items-center gap-1 text-primary hover:underline text-xs font-medium"
                                      data-testid={`download-payment-receipt-${p.id}`}
                                      title="Download receipt PDF"
                                    >
                                      <ReceiptIcon className="w-3.5 h-3.5" />
                                      {p.receipt_number}
                                    </button>
                                    <button
                                      onClick={() => handleIssueReceipt(p)}
                                      disabled={issuingReceiptId === p.id}
                                      className="text-foreground/50 hover:text-primary disabled:opacity-40"
                                      data-testid={`resend-payment-receipt-${p.id}`}
                                      title="Re-send receipt via email"
                                    >
                                      <Send className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => handleIssueReceipt(p)}
                                    disabled={issuingReceiptId === p.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-primary text-primary rounded hover:bg-primary/5 transition-colors disabled:opacity-50"
                                    data-testid={`issue-payment-receipt-${p.id}`}
                                    title="Create receipt PDF and email to client"
                                  >
                                    <Mail className="w-3 h-3" />
                                    {issuingReceiptId === p.id ? 'Issuing…' : 'Issue Receipt'}
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-2 text-foreground/70 text-xs max-w-xs truncate" title={p.notes || ''}>
                                {p.notes || '—'}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <div className="inline-flex items-center gap-2">
                                  <button
                                    onClick={() => { setEditingPayment(p); setShowPaymentModal(true); }}
                                    className="text-foreground/50 hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid={`edit-payment-${p.id}`}
                                    disabled={p.receipt_kind === 'combined'}
                                    title={p.receipt_kind === 'combined' ? 'Part of an issued combined receipt; allocation is read-only' : 'Edit payment'}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeletePayment(p.id)}
                                    disabled={deletingPaymentId === p.id || p.receipt_kind === 'combined'}
                                    className="text-red-600 hover:text-red-700 disabled:opacity-40"
                                    data-testid={`delete-payment-${p.id}`}
                                    title={p.receipt_kind === 'combined' ? 'Part of an issued combined receipt; allocation cannot be removed individually' : 'Remove payment'}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Notes */}
            {quote.notes && (
              <div className="bg-white rounded-xl border border-border shadow-sm p-6">
                <h2 className="text-lg font-bold text-foreground mb-4">Notes</h2>
                <p className="text-foreground/70 whitespace-pre-wrap">{quote.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-xl p-6 border border-border shadow-sm sticky top-6 space-y-4">
              <h3 className="text-lg font-bold text-foreground mb-4">Actions</h3>
              
              <button
                onClick={handleDownloadPDF}
                className="w-full px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </button>

              {quote.acceptance?.typed_name && (
                <button
                  onClick={handleDownloadSignatureAudit}
                  data-testid="signature-audit-sidebar-btn"
                  className="w-full px-4 py-3 border-2 border-emerald-300 bg-emerald-50 text-emerald-800 rounded-lg hover:bg-emerald-100 transition-colors flex items-center justify-center gap-2"
                >
                  <ShieldCheck className="w-5 h-5" />
                  Signature Audit
                </button>
              )}

              <button
                onClick={() => navigate(`/admin/quotes/${id}/edit`)}
                className="w-full px-4 py-3 border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
              >
                <Edit className="w-5 h-5" />
                Edit Quote
              </button>

              <button
                onClick={handleSendEmail}
                disabled={sending}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Mail className="w-5 h-5" />
                {sending ? 'Sending Email...' : 'Send to Client'}
              </button>

              <button
                onClick={handleSendWhatsApp}
                data-testid="whatsapp-quote-sidebar-btn"
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Send via WhatsApp
              </button>

              {!quote.converted_to_invoice && (
                <button
                  onClick={handleConvertToInvoice}
                  disabled={converting}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <FileCheck className="w-5 h-5" />
                  {converting ? 'Converting...' : 'Convert to Invoice'}
                </button>
              )}

              {(() => {
                const totalPaid = (quote.payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                if (totalPaid <= 0) return null;
                return (
                  <button
                    onClick={() => setRefundOpen(true)}
                    data-testid="open-refund-from-quote-btn"
                    className="w-full px-4 py-3 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                    title="Issue a refund against the deposits / partial payments on this quote"
                  >
                    <RefreshCcw className="w-5 h-5" />
                    Issue Refund
                  </button>
                );
              })()}
            </div>

            {/* Client Info */}
            <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
              <h3 className="text-lg font-bold text-foreground mb-4">Client Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-foreground/60">Name</p>
                  <p className="font-medium text-foreground">{quote.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Email</p>
                  <p className="font-medium text-foreground">{quote.client_email}</p>
                </div>
                {quote.client_phone && (
                  <div>
                    <p className="text-sm text-foreground/60">Phone</p>
                    <p className="font-medium text-foreground">{quote.client_phone}</p>
                  </div>
                )}
                {quote.client_address && (
                  <div>
                    <p className="text-sm text-foreground/60">Address</p>
                    <p className="font-medium text-foreground">{quote.client_address}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPaymentModal && (
        <AddPaymentModal
          quote={quote}
          password={password}
          editingPayment={editingPayment}
          onClose={() => { setShowPaymentModal(false); setEditingPayment(null); }}
          onSaved={(updated) => {
            setQuote(updated);
            setShowPaymentModal(false);
            setEditingPayment(null);
          }}
        />
      )}

      {historyPayment && (
        <PaymentHistoryModal
          payment={historyPayment}
          currency={quote.currency}
          onClose={() => setHistoryPayment(null)}
        />
      )}

      <IssueRefundModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        sourceType="quote_payment"
        sourceId={quote.id}
        sourceNumber={quote.quote_number}
        paidAmount={(quote.payments || []).reduce((s, p) => s + (p.amount || 0), 0)}
        currency={quote.currency || 'CI$'}
        clientName={quote.client_name}
        onIssued={() => alert(`Refund issued and emailed to ${quote.client_email}.`)}
      />
    </div>
  );
};

// =========================================================
// Add/Edit Payment Modal (deposits / partial payments)
// =========================================================
const AddPaymentModal = ({ quote, password, editingPayment = null, onClose, onSaved }) => {
  const isEditing = Boolean(editingPayment);
  const payments = quote.payments || [];
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  // In edit mode the remaining baseline excludes the payment being edited
  const baselinePaid = isEditing ? totalPaid - (editingPayment.amount || 0) : totalPaid;
  const remaining = Math.max((quote.total || 0) - baselinePaid, 0);

  const today = caymanToday();
  const initialDate = isEditing && editingPayment.payment_date
    ? caymanDateKey(editingPayment.payment_date)
    : today;
  const [form, setForm] = useState({
    amount: isEditing
      ? (editingPayment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : (remaining > 0 ? remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''),
    payment_method: isEditing ? (editingPayment.payment_method || 'bank_transfer') : 'bank_transfer',
    payment_date: initialDate,
    notes: isEditing ? (editingPayment.notes || '') : '',
    auto_issue_receipt: !isEditing, // only relevant when creating
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // After successful save, hold the response so we can optionally issue the receipt in a second step
  const [success, setSuccess] = useState(null); // { quote, payment }
  const [issuingReceipt, setIssuingReceipt] = useState(false);

  const applyPreset = (percent) => {
    const baseline = quote.total || 0;
    const amt = percent === 'remaining' ? remaining : (baseline * percent) / 100;
    setForm((f) => ({ ...f, amount: amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
  };

  // Live calculations driven by whatever the user has typed in the Amount field.
  // We expose them in the modal as a clear preview so the admin can see exactly
  // what percentage of the quote total is being paid and what will be left over.
  const enteredAmount = parseFloat(form.amount) || 0;
  const enteredPercentage = quote.total > 0
    ? Math.round((enteredAmount / quote.total) * 1000) / 10  // 1 decimal place
    : 0;
  const newTotalPaid = baselinePaid + enteredAmount;
  const newRemaining = Math.max((quote.total || 0) - newTotalPaid, 0);
  const newCumulativePct = quote.total > 0
    ? Math.round((newTotalPaid / quote.total) * 1000) / 10
    : 0;

  // Allow user to type a percentage and have the amount auto-fill
  const handlePercentageChange = (pctStr) => {
    const pct = parseFloat(pctStr);
    if (!isFinite(pct) || pct < 0) {
      setForm((f) => ({ ...f, amount: '' }));
      return;
    }
    const amt = ((quote.total || 0) * pct) / 100;
    setForm((f) => ({ ...f, amount: amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
  };

  const presets = [
    { label: '25%', value: 25 },
    { label: '50%', value: 50 },
    { label: '75%', value: 75 },
    { label: '100%', value: 100 },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      setError('Please enter a valid amount greater than 0.');
      return;
    }
    setSaving(true);
    try {
      const url = isEditing
        ? `${API_URL}/api/admin/quotes/${quote.id}/payments/${editingPayment.id}`
        : `${API_URL}/api/admin/quotes/${quote.id}/payments`;
      const method = isEditing ? 'PUT' : 'POST';
      const body = isEditing
        ? {
            amount: amt,
            payment_method: form.payment_method,
            payment_date: form.payment_date, // plain YYYY-MM-DD — preserve calendar day
            notes: form.notes || null,
          }
        : {
            amount: amt,
            payment_method: form.payment_method,
            payment_date: form.payment_date, // plain YYYY-MM-DD — preserve calendar day
            notes: form.notes || null,
            auto_issue_receipt: form.auto_issue_receipt,
          };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`,
        },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const data = await response.json();
        if (isEditing) {
          onSaved(data);
          return;
        }
        // Creation flow — show success panel
        const newPayment = (data.payments || []).slice(-1)[0] || null;
        setSuccess({ quote: data, payment: newPayment });
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.detail || (isEditing ? 'Failed to update payment' : 'Failed to record payment'));
      }
    } catch (err) {
      logger.error('Record/edit payment error:', err);
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const issueReceiptNow = async () => {
    if (!success?.payment) return;
    setIssuingReceipt(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/quotes/${quote.id}/payments/${success.payment.id}/issue-receipt`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${btoa(':' + password)}`,
          },
          body: JSON.stringify({ send_email: true }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        const updatedPayment = (data.payments || []).find((p) => p.id === success.payment.id) || success.payment;
        setSuccess({ quote: data, payment: updatedPayment });
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.detail || 'Failed to issue receipt');
      }
    } catch (err) {
      logger.error('Issue receipt error:', err);
      setError('Failed to issue receipt. Please try again.');
    } finally {
      setIssuingReceipt(false);
    }
  };

  const closeWithSave = () => {
    if (success?.quote) {
      onSaved(success.quote);
    } else {
      onClose();
    }
  };

  // --- Success state ---
  if (success) {
    const p = success.payment;
    const hasReceipt = Boolean(p?.receipt_issued && p?.receipt_number);
    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={closeWithSave}
        data-testid="add-payment-modal"
      >
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-green-600" />
              Payment Recorded
            </h3>
            <button
              type="button"
              onClick={closeWithSave}
              className="text-foreground/50 hover:text-foreground"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="font-semibold text-green-900">
                {quote.currency}{(p?.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} recorded against {quote.quote_number}.
              </p>
              <p className="text-sm text-green-800/80 mt-1">
                New status: <span className="font-medium">{(success.quote.deposit_status || 'none').replace(/^./, (c) => c.toUpperCase())}</span>
                {' · '}Remaining: <span className="font-medium">{quote.currency}{Math.max((success.quote.total || 0) - (success.quote.deposit_amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </p>
            </div>

            {hasReceipt ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <ReceiptIcon className="w-5 h-5 text-blue-700 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-blue-900">Receipt {p.receipt_number} issued</p>
                  <p className="text-blue-800/80">Emailed to {quote.client_email}.</p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-amber-900 mb-1">No receipt was issued.</p>
                <p className="text-sm text-amber-800/90 mb-3">
                  Send a receipt PDF to {quote.client_email} now, or do it later from the ledger.
                </p>
                <button
                  type="button"
                  onClick={issueReceiptNow}
                  disabled={issuingReceipt}
                  data-testid="success-issue-receipt-btn"
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Mail className="w-4 h-4" />
                  {issuingReceipt ? 'Issuing receipt…' : 'Issue Receipt & Email Now'}
                </button>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {error}
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={closeWithSave}
              data-testid="success-close-btn"
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-payment-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              {isEditing ? 'Edit Payment' : 'Record Payment'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-foreground/50 hover:text-foreground"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
              <p className="text-foreground/70">Quote total: <span className="font-semibold text-foreground">{quote.currency}{(quote.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
              <p className="text-foreground/70">Already paid: <span className="font-semibold text-foreground">{quote.currency}{totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
              <p className="text-foreground/70">Outstanding: <span className="font-semibold text-primary">{quote.currency}{remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground/70 uppercase tracking-wide mb-2">
                Quick presets (of quote total)
              </label>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    type="button"
                    key={p.value}
                    onClick={() => applyPreset(p.value)}
                    className="px-3 py-1.5 text-sm border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors"
                    data-testid={`preset-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => applyPreset('remaining')}
                  className="px-3 py-1.5 text-sm border border-primary bg-primary/5 text-primary rounded-lg hover:bg-primary/10 transition-colors"
                  data-testid="preset-remaining"
                >
                  Remaining ({quote.currency}{remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-foreground/70 mb-1">Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50 text-sm">{quote.currency}</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="payment-amount-input"
                    className="w-full pl-10 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-1">% of total</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={enteredPercentage || ''}
                    onChange={(e) => handlePercentageChange(e.target.value)}
                    data-testid="payment-percentage-input"
                    placeholder="0"
                    className="w-full pl-3 pr-7 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/50 text-sm">%</span>
                </div>
              </div>
            </div>

            {/* Live preview of what this payment means */}
            {enteredAmount > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1.5" data-testid="payment-preview">
                <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">After this payment</p>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-emerald-800/70 text-xs">This payment</p>
                    <p className="font-semibold text-emerald-900">
                      {quote.currency}{enteredAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-xs font-normal">({enteredPercentage}%)</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-emerald-800/70 text-xs">Total paid</p>
                    <p className="font-semibold text-emerald-900">
                      {quote.currency}{newTotalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-xs font-normal">({newCumulativePct}%)</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-emerald-800/70 text-xs">Remaining</p>
                    <p className={`font-semibold ${newRemaining < 0.01 ? 'text-emerald-900' : 'text-foreground'}`}>
                      {quote.currency}{newRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {newRemaining < 0.01 && <span className="text-xs font-medium ml-1">✓ Paid in full</span>}
                    </p>
                  </div>
                </div>
                {/* Mini progress bar showing where this payment lands */}
                <div className="w-full h-1.5 bg-emerald-100 rounded-full overflow-hidden mt-1">
                  <div
                    className="h-full bg-emerald-600 transition-all duration-300"
                    style={{ width: `${Math.min(newCumulativePct, 100)}%` }}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">Payment Date *</label>
              <input
                type="date"
                required
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                data-testid="payment-date-input"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">Payment Method</label>
              <select
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                data-testid="payment-method-select"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="wire">Wire Transfer</option>
                <option value="cash">Cash</option>
                <option value="card">Credit/Debit Card</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">Notes (optional)</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows="2"
                placeholder="e.g. 50% upfront deposit"
                data-testid="payment-notes-input"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {!isEditing && (
              <label className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.auto_issue_receipt}
                  onChange={(e) => setForm({ ...form, auto_issue_receipt: e.target.checked })}
                  data-testid="auto-issue-receipt-checkbox"
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="font-semibold text-green-900">Auto-issue receipt PDF</span>
                  <span className="block text-green-800/80 text-xs">
                    Generate a receipt, save it in the Receipts section, and email it to {quote.client_email}.
                  </span>
                </span>
              </label>
            )}

            {isEditing && editingPayment?.receipt_issued && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <ReceiptIcon className="w-5 h-5 text-blue-700 mt-0.5 flex-shrink-0" />
                <span className="text-sm">
                  <span className="font-semibold text-blue-900">
                    Receipt {editingPayment.receipt_number} will be updated
                  </span>
                  <span className="block text-blue-800/80 text-xs">
                    The linked receipt (amount, method, date, and balance) is automatically synced to match your edits. No new email is sent — use the send icon on the ledger row if you need to re-send it.
                  </span>
                </span>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {error}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-payment-btn"
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Wallet className="w-4 h-4" />
              {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Record Payment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =========================================================
// Payment History Modal — shows the per-payment audit trail
// =========================================================
const FIELD_LABELS = {
  amount: 'Amount',
  payment_method: 'Method',
  payment_date: 'Payment date',
  notes: 'Notes',
};

const METHOD_LABELS_LOCAL = {
  bank_transfer: 'Bank Transfer',
  wire: 'Wire Transfer',
  cash: 'Cash',
  card: 'Credit/Debit Card',
  other: 'Other',
};

const formatChangeValue = (field, value, currency) => {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'amount') return `${currency}${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (field === 'payment_method') return METHOD_LABELS_LOCAL[value] || value;
  if (field === 'payment_date') {
    try {
      return new Date(value).toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return value;
    }
  }
  return String(value);
};

const formatChangedAt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const PaymentHistoryModal = ({ payment, currency, onClose }) => {
  const history = payment.history || [];
  // Newest first
  const entries = [...history].reverse();

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="payment-history-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
            <History className="w-5 h-5 text-amber-600" />
            Change history
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-foreground/50 hover:text-foreground"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-6 overflow-y-auto space-y-5">
          <p className="text-xs text-foreground/60">
            Payment created{' '}
            <span className="font-medium text-foreground">{formatChangedAt(payment.created_at)}</span>
            {payment.receipt_number && (
              <> · linked to <span className="font-mono text-primary">{payment.receipt_number}</span></>
            )}
          </p>

          {entries.length === 0 ? (
            <p className="text-sm text-foreground/60">No edit history recorded.</p>
          ) : (
            <ol className="relative border-l-2 border-amber-200 pl-5 space-y-5">
              {entries.map((entry, idx) => {
                const fields = Object.entries(entry.changes || {});
                return (
                  <li key={`${payment.id}-h-${idx}`} className="relative">
                    <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-white" />
                    <p className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
                      {formatChangedAt(entry.changed_at)}
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {fields.map(([field, delta]) => (
                        <li
                          key={`${payment.id}-h-${idx}-${field}`}
                          className="text-sm text-foreground"
                          data-testid={`history-change-${field}`}
                        >
                          <span className="font-medium text-foreground/80">
                            {FIELD_LABELS[field] || field}:
                          </span>{' '}
                          <span className="text-foreground/60 line-through">
                            {formatChangeValue(field, delta.old, currency)}
                          </span>{' '}
                          <span className="text-foreground/40">→</span>{' '}
                          <span className="font-semibold text-primary">
                            {formatChangeValue(field, delta.new, currency)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

